// The camera rig every move shares (WS-49 T4): small steps round the point the camera looks at, inside the zoom
// limits of the controls. It works on the engine's camera and controls as they are and imports nothing, so a move
// never touches three.js. The camera bounds (camerabounds.js) still run after every frame, as for any camera writer.

/** The engine's flight ease (main.js `easeInOut`): at rest at both ends. */
export const easeInOut = (k) => (k < 0.5 ? 4 * k ** 3 : 1 - (-2 * k + 2) ** 3 / 2);

/**
 * 0..1 over a stay of `len` seconds: rises from rest after arrival and falls back to rest before the tour leaves,
 * so a steady motion multiplied by it never starts or stops suddenly.
 * @param {number} t    seconds since arrival
 * @param {number} len  length of the stay
 */
export function ramp(t, len) {
  const r = Math.min(1.5, len / 3);
  if (!(r > 0)) return 1;
  const k = Math.max(0, Math.min(1, t / r, (len - t) / r));
  return k * k * (3 - 2 * k);
}

/** How fast a move goes, in the builder's three words (`moveOptions.speed`): a factor on the move's own default. */
export const SPEED = { slow: 0.5, medium: 1, fast: 2 };
/** The factor of `moveOptions.speed`; anything else = 1 (the move's own default). */
export const speedOf = (o) => (Object.hasOwn(SPEED, o?.speed) ? SPEED[o.speed] : 1);

/**
 * A view as plain numbers (VIS-265): where the camera is, the point it looks at, its lens (degrees), how far it looks
 * up from there (`lookUp`, degrees, headlook.js). Local metres.
 * @typedef {{ position: number[], target: number[], fov?: number, lookUp?: number }} View
 */
const D2R = Math.PI / 180;
/** `v` turned `deg` degrees round the vertical axis through `c`, clockwise seen from above (north to east). */
export function turnAbout(v, c, deg) {
  const a = deg * D2R, cs = Math.cos(a), sn = Math.sin(a), x = v[0] - c[0], z = v[2] - c[2];
  return [c[0] + x * cs - z * sn, v[1], c[2] + x * sn + z * cs];
}

/**
 * VIS-265: a move written as a pose, `pose(view, k, options)` (k = 0..1 of the stay, eased by the move), played as a
 * tour stop's move: from the view the camera arrived at, to the pose at the end of the stay. Film reads `pose` alone.
 */
export function posed(pose, o, rig) {
  let from = null;
  return {
    travel: Number.isFinite(o.travel) ? Math.max(0, o.travel) : 3.2,
    hold: false,
    // the stop's pivot (`rig.around`: its place, or the point picked in the builder) is the pose's `center`
    step(dt, t, len) { from ||= rig.view(); rig.set(pose(from, len > 0 ? Math.min(1, t / len) : 1, { ...o, center: rig.pivot() })); },
  };
}

/**
 * @param {object} camera    THREE.PerspectiveCamera
 * @param {object} controls  MapControls (`target`, `minDistance`, `maxDistance`)
 * @param {{ get: () => number, set: (deg: number) => void } | null} [head]  the look-up the tour gives the picture (degrees)
 */
export function createRig(camera, controls, head = null) {
  let pivot = null; // { x, y, z } the point the moves turn round and move towards; null = the target the camera looks at
  return {
    /** VIS-265: the view now, as plain numbers. */
    view: () => ({ position: camera.position.toArray(), target: controls.target.toArray(), fov: camera.fov, ...(head && { lookUp: head.get() }) }),
    /** VIS-265: puts the camera at a view (a move's step); a lens and a look-up only when the view has them (From A to B). */
    set(v) {
      camera.position.fromArray(v.position); controls.target.fromArray(v.target);
      if (v.fov > 0 && v.fov !== camera.fov) { camera.fov = v.fov; camera.updateProjectionMatrix(); }
      if (head && Number.isFinite(v.lookUp) && v.lookUp !== head.get()) head.set(v.lookUp);
    },
    /** VIS-265: the point set with `around`, as [x, y, z]; null = none (the target). */
    pivot: () => (pivot ? [pivot.x, pivot.y, pivot.z] : null),
    /**
     * The point the next steps turn round (the tour engine sets it for each stop: the stop's place, or the point
     * picked in the builder). The whole picture turns round it: the camera AND the point it looks at, so the
     * framing of the saved view is kept and the thing at the pivot stays in the picture. Null: the target.
     */
    around(p) { pivot = p && [p.x, p.y, p.z].every(Number.isFinite) ? { x: p.x, y: p.y, z: p.z } : null; },
    /**
     * Turns the camera `angle` radians round the vertical axis through the pivot (the height stays). `scale` moves
     * it nearer (< 1) or further away from the pivot, the framing kept, never past the zoom limits.
     */
    turn(angle, scale = 1) {
      const p = camera.position, t = controls.target, c = pivot || t, cs = Math.cos(angle), sn = Math.sin(angle);
      if (scale !== 1) {
        const d = Math.hypot(p.x - t.x, p.y - t.y, p.z - t.z);
        const k = d > 0 ? Math.min(Math.max(d * scale, controls.minDistance ?? 0), controls.maxDistance ?? Infinity) / d : 1;
        for (const v of pivot ? [p, t] : [p]) v.set(c.x + (v.x - c.x) * k, c.y + (v.y - c.y) * k, c.z + (v.z - c.z) * k);
      }
      for (const v of pivot ? [p, t] : [p]) {
        const x = v.x - c.x, z = v.z - c.z;
        v.set(c.x + x * cs + z * sn, v.y, c.z + z * cs - x * sn);
      }
    },
  };
}
